import {
  SAVED_DEFAULT_VIEW,
  SAVED_TAB_DEFAULT,
  SAVED_TYPE_DEFAULT,
} from "@/constants/saved"
import {
  Collection,
  CollectionRef,
  CollectionTrigger,
} from "@/types/collection"
import { PlaceRef } from "@/types/entity"
import { MapMarker } from "@/types/map-marker"
import { SavedSearchResults } from "@/types/page"
import { Place, PlaceSummary } from "@/types/place"
import { SavedTab, SavedType, SavedView } from "@/types/saved"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { formatPlaceType } from "@/lib/format-utils"
import {
  isPlaceBasedMarker,
  mapMarkerEntityRef,
  mapMarkerPlace,
} from "@/lib/map-marker-utils"
import { selectSavedPlaceRefs } from "@/lib/storage/api/saved-places-api"
import { RootState } from "@/lib/storage/redux-store"
import { selectTripPageActive } from "./detail-sheet-slice"

export interface CollectionDialogState {
  name: string
  isPrivate: boolean
  isLoading: boolean
  error: string
}

export interface SavedSliceState {
  loading: boolean
  searchString: string
  visiblePlaces: PlaceSummary[]
  currentTab: SavedTab
  currentView: SavedView
  placeSearchResults: SavedSearchResults | undefined
  placeSearchRequestId: string | undefined
  selectedType: SavedType
  mapMarkers: MapMarker[]
  isBboxSearchInProgress: boolean
  saveDialogOpen: boolean
  saveDialogPlace?: Place
  saveDialogPlaceRefs?: PlaceRef[]
  collectionDialogOpen: boolean
  collectionTrigger?: CollectionTrigger
  lastUsedCollection?: Collection
  editingCollection?: Collection
  collectionDialog: CollectionDialogState
  pendingCollectionImport: boolean
  pendingCollectionImports: CollectionRef[]
  importGooglePinsDialogOpen: boolean
  importTravelResourceDialogOpen: boolean
  autoOpenImport: boolean
}

export const savedSliceInitialState: SavedSliceState = {
  loading: true,
  searchString: "",
  currentTab: SAVED_TAB_DEFAULT,
  currentView: SAVED_DEFAULT_VIEW,
  visiblePlaces: [],
  placeSearchResults: undefined,
  placeSearchRequestId: undefined,
  selectedType: SAVED_TYPE_DEFAULT,
  mapMarkers: [],
  isBboxSearchInProgress: false,
  saveDialogOpen: false,
  collectionDialogOpen: false,
  collectionDialog: {
    name: "",
    isPrivate: false,
    isLoading: false,
    error: "",
  },
  pendingCollectionImport: false,
  pendingCollectionImports: [],
  importGooglePinsDialogOpen: false,
  importTravelResourceDialogOpen: false,
  autoOpenImport: false,
}

const savedSlice = createSlice({
  name: "savedSlice",
  initialState: savedSliceInitialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setCurrentTab: (state, action: PayloadAction<SavedTab>) => {
      state.currentTab = action.payload
    },
    setCurrentView: (state, action: PayloadAction<SavedView>) => {
      state.currentView = action.payload
    },
    setPlaceSearchResults: (
      state,
      action: PayloadAction<SavedSearchResults | undefined>
    ) => {
      state.placeSearchResults = action.payload
    },
    setPlaceSearchRequestId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.placeSearchRequestId = action.payload
    },
    setSelectedType: (state, action: PayloadAction<SavedType>) => {
      state.selectedType = action.payload
    },
    setVisiblePlaces: (state, action: PayloadAction<PlaceSummary[]>) => {
      state.visiblePlaces = action.payload
    },
    setSearchString: (state, action: PayloadAction<string>) => {
      state.searchString = action.payload
    },
    setMapMarkers: (state, action: PayloadAction<MapMarker[]>) => {
      state.mapMarkers = action.payload
    },
    setIsBboxSearchInProgress: (state, action: PayloadAction<boolean>) => {
      state.isBboxSearchInProgress = action.payload
    },
    setSaveDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.saveDialogOpen = action.payload
      if (!action.payload && !state.collectionDialogOpen) {
        state.saveDialogPlace = undefined
        state.saveDialogPlaceRefs = undefined
      }
    },
    setSaveDialogPlace: (state, action: PayloadAction<Place | undefined>) => {
      state.saveDialogPlace = action.payload
    },
    setSaveDialogPlaceRefs: (
      state,
      action: PayloadAction<PlaceRef[] | undefined>
    ) => {
      state.saveDialogPlaceRefs = action.payload
    },
    setCollectionDialogOpen: (
      state,
      action: PayloadAction<
        boolean | { open: boolean; trigger: CollectionTrigger }
      >
    ) => {
      if (typeof action.payload === "boolean") {
        state.collectionDialogOpen = action.payload
        if (!action.payload) {
          state.editingCollection = undefined
          state.saveDialogPlace = undefined
          state.saveDialogPlaceRefs = undefined
          state.collectionTrigger = undefined
        }
      } else {
        state.collectionDialogOpen = action.payload.open
        state.collectionTrigger = action.payload.trigger
        if (!action.payload.open) {
          state.editingCollection = undefined
          state.saveDialogPlace = undefined
          state.saveDialogPlaceRefs = undefined
          state.collectionTrigger = undefined
        }
      }
    },
    setEditingCollection: (
      state,
      action: PayloadAction<Collection | undefined>
    ) => {
      state.editingCollection = action.payload
    },
    setLastUsedCollection: (
      state,
      action: PayloadAction<Collection | undefined>
    ) => {
      state.lastUsedCollection = action.payload
    },
    setCollectionDialogName: (state, action: PayloadAction<string>) => {
      state.collectionDialog.name = action.payload
    },
    setCollectionDialogIsPrivate: (state, action: PayloadAction<boolean>) => {
      state.collectionDialog.isPrivate = action.payload
    },
    setCollectionDialogIsLoading: (state, action: PayloadAction<boolean>) => {
      state.collectionDialog.isLoading = action.payload
    },
    setCollectionDialogError: (state, action: PayloadAction<string>) => {
      state.collectionDialog.error = action.payload
    },
    setPendingCollectionImport: (
      state,
      action: PayloadAction<boolean | CollectionRef>
    ) => {
      if (typeof action.payload === "boolean") {
        state.pendingCollectionImport = action.payload
        if (action.payload === false) {
          state.importGooglePinsDialogOpen = false
          state.importTravelResourceDialogOpen = false
        }
      } else {
        if (!state.pendingCollectionImports.includes(action.payload)) {
          state.pendingCollectionImports.push(action.payload)
        }
      }
    },
    removePendingCollectionImport: (
      state,
      action: PayloadAction<CollectionRef>
    ) => {
      state.pendingCollectionImports = state.pendingCollectionImports.filter(
        (ref) => ref !== action.payload
      )
    },
    setImportGooglePinsDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.importGooglePinsDialogOpen = action.payload
    },
    setImportTravelResourceDialogOpen: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.importTravelResourceDialogOpen = action.payload
    },
    setAutoOpenImport: (state, action: PayloadAction<boolean>) => {
      state.autoOpenImport = action.payload
    },
  },
})

const selectSliceState = (state: RootState) => state.savedSlice

export const selectLoading = createSelector(
  [selectSliceState],
  (state) => state.loading
)

export const selectSelectedType = createSelector(
  [selectSliceState],
  (state) => state.selectedType
)

export const selectSearchString = createSelector(
  [selectSliceState],
  (state) => state.searchString
)

export const selectVisiblePlaces = createSelector(
  [selectSliceState],
  (state) => state.visiblePlaces
)

export const selectVisiblePlacesCount = createSelector(
  [selectVisiblePlaces],
  (visiblePlaces) => visiblePlaces.length
)

export const selectPlaceSearchResultsRaw = createSelector(
  [selectSliceState],
  (state) => state.placeSearchResults
)

export const selectPlaceSearchResults = createSelector(
  [selectPlaceSearchResultsRaw, selectSavedPlaceRefs],
  (searchResults, savedRefs) => {
    if (!searchResults) return undefined
    return {
      ...searchResults,
      data: searchResults.data.filter((place) =>
        savedRefs.has(place.entity_ref)
      ),
    }
  }
)

export const selectCurrentTab = createSelector(
  [selectSliceState],
  (state) => state.currentTab
)

export const selectCurrentView = createSelector(
  [selectSliceState],
  (state) => state.currentView
)

export const selectFilteredCount = createSelector(
  [
    selectPlaceSearchResultsRaw,
    selectPlaceSearchResults,
    selectVisiblePlacesCount,
    selectTripPageActive,
    selectCurrentView,
  ],
  (
    rawResults,
    filteredResults,
    visiblePlacesCount,
    tripSheetOpen,
    currentView
  ) => {
    if (tripSheetOpen && currentView === "trips") {
      return visiblePlacesCount
    }
    if (!rawResults || !filteredResults) return undefined

    // Calculate how many places were removed by client-side filtering
    const removedCount = rawResults.data.length - filteredResults.data.length

    // Subtract removed places from server's total count
    const serverTotal = rawResults.meta.total_count ?? rawResults.data.length
    return Math.max(0, serverTotal - removedCount)
  }
)

export const selectFormattedType = createSelector(
  [selectSelectedType],
  (selectedType) => {
    switch (selectedType) {
      case "all":
        return "places"
      default:
        return formatPlaceType(selectedType)
    }
  }
)

export const selectMapMarkers = createSelector(
  [selectSliceState],
  (state) => state.mapMarkers
)

export const selectFilteredMapMarkers = createSelector(
  [
    selectSearchString,
    selectMapMarkers,
    selectSelectedType,
    selectPlaceSearchResults,
  ],
  (searchString, mapMarkers, selectedType, searchResults) => {
    if (!mapMarkers) return []

    let filteredMarkers = mapMarkers

    if (selectedType !== "all") {
      filteredMarkers = filteredMarkers.filter((marker) => {
        if (!isPlaceBasedMarker(marker)) return false
        return mapMarkerPlace(marker).type === selectedType
      })
    }

    if (searchString && searchResults?.meta.total_count) {
      const searchResultEntityRefs = new Set(
        searchResults.data.map((place) => place.entity_ref)
      )

      filteredMarkers = filteredMarkers.filter((marker) => {
        const entityRef = mapMarkerEntityRef(marker)
        return entityRef ? searchResultEntityRefs.has(entityRef) : false
      })
    }

    return filteredMarkers
  }
)

export const selectIsBboxSearchInProgress = createSelector(
  [selectSliceState],
  (state) => state.isBboxSearchInProgress
)

export const selectSavedDialogOpen = createSelector(
  [selectSliceState],
  (state) => state.saveDialogOpen
)

export const selectCollectionDialogOpen = createSelector(
  [selectSliceState],
  (state) => state.collectionDialogOpen
)

export const selectSaveDialogPlace = createSelector(
  [selectSliceState],
  (state) => state.saveDialogPlace
)

export const selectSaveDialogPlaceRefs = createSelector(
  [selectSliceState],
  (state) => state.saveDialogPlaceRefs
)

export const selectLastUsedCollection = createSelector(
  [selectSliceState],
  (state) => state.lastUsedCollection
)

export const selectEditingCollection = createSelector(
  [selectSliceState],
  (state) => state.editingCollection
)

export const selectCollectionTrigger = createSelector(
  [selectSliceState],
  (state) => state.collectionTrigger
)

export const selectCollectionDialogState = createSelector(
  [selectSliceState],
  (state) => state.collectionDialog
)

export const selectCollectionDialogName = createSelector(
  [selectCollectionDialogState],
  (state) => state.name
)

export const selectCollectionDialogIsPrivate = createSelector(
  [selectCollectionDialogState],
  (state) => state.isPrivate
)

export const selectCollectionDialogIsLoading = createSelector(
  [selectCollectionDialogState],
  (state) => state.isLoading
)

export const selectCollectionDialogError = createSelector(
  [selectCollectionDialogState],
  (state) => state.error
)

export const selectPendingCollectionImport = createSelector(
  [selectSliceState],
  (state) => state.pendingCollectionImport
)

export const selectPendingCollectionImports = createSelector(
  [selectSliceState],
  (state) => state.pendingCollectionImports
)

export const selectIsCollectionImporting = (collectionRef: CollectionRef) =>
  createSelector([selectSliceState], (state) =>
    state.pendingCollectionImports.includes(collectionRef)
  )

export const selectImportGooglePinsDialogOpen = createSelector(
  [selectSliceState],
  (state) => state.importGooglePinsDialogOpen
)

export const selectImportTravelResourceDialogOpen = createSelector(
  [selectSliceState],
  (state) => state.importTravelResourceDialogOpen
)

export const selectAutoOpenFileImport = createSelector(
  [selectSliceState],
  (state) => state.autoOpenImport
)

export const {
  setLoading,
  setCurrentTab,
  setCurrentView,
  setPlaceSearchResults,
  setPlaceSearchRequestId,
  setSelectedType,
  setVisiblePlaces,
  setSearchString,
  setMapMarkers,
  setIsBboxSearchInProgress,
  setSaveDialogOpen,
  setSaveDialogPlace,
  setSaveDialogPlaceRefs,
  setCollectionDialogOpen,
  setEditingCollection,
  setLastUsedCollection,
  setCollectionDialogName,
  setCollectionDialogIsPrivate,
  setCollectionDialogIsLoading,
  setCollectionDialogError,
  setPendingCollectionImport,
  removePendingCollectionImport,
} = savedSlice.actions

export default savedSlice
